import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Bot, Loader2, PlayCircle, Check, X, Trash2, Plus, RefreshCw, Rss, ListChecks,
  Activity, Settings2, CheckCircle2, AlertCircle, ExternalLink, Zap,
} from "lucide-react";

const CATEGORIES = [
  "राजनीति","राष्ट्रीय","अंतरराष्ट्रीय","खेल","मनोरंजन","व्यापार",
  "टेक्नोलॉजी","अपराध","स्वास्थ्य","शिक्षा","कृषि","सामान्य",
];

const TAB_LIST = [
  { id: "queue", label: "Review Queue", icon: ListChecks },
  { id: "sources", label: "Sources", icon: Rss },
  { id: "runs", label: "Runs Log", icon: Activity },
  { id: "settings", label: "Settings", icon: Settings2 },
];

export default function AutoNewsPage() {
  const [tab, setTab] = useState("queue");
  const [queue, setQueue] = useState([]);
  const [queueLoading, setQueueLoading] = useState(false);
  const [sources, setSources] = useState([]);
  const [runs, setRuns] = useState([]);
  const [settings, setSettings] = useState({ auto_news_enabled: false, auto_news_frequency_min: 30 });
  const [runBusy, setRunBusy] = useState(false);
  const [editing, setEditing] = useState(null); // queue item being edited
  const [newSrc, setNewSrc] = useState({ name: "", url: "", category: "राष्ट्रीय", auto_publish: false });

  const loadQueue = async () => {
    setQueueLoading(true);
    try { const { data } = await api.get("/admin/auto-news/queue", { params: { status: "pending", limit: 100 }}); setQueue(data || []); }
    catch (e) { toast.error(formatError(e)); } finally { setQueueLoading(false); }
  };
  const loadSources = async () => {
    try { const { data } = await api.get("/admin/auto-news/sources"); setSources(data || []); } catch (e) { /* silent */ }
  };
  const loadRuns = async () => {
    try { const { data } = await api.get("/admin/auto-news/runs", { params: { limit: 20 } }); setRuns(data || []); } catch (e) {}
  };
  const loadSettings = async () => {
    try { const { data } = await api.get("/admin/auto-news/settings"); setSettings(data); } catch (e) {}
  };
  useEffect(() => { loadQueue(); loadSources(); loadRuns(); loadSettings(); }, []);

  const runNow = async () => {
    setRunBusy(true);
    try {
      await api.post("/admin/auto-news/run-now");
      toast.success("Run background में शुरू — 1-3 min me queue update hoga");
      setTimeout(() => { loadQueue(); loadRuns(); setRunBusy(false); }, 90000);
    } catch (e) { toast.error(formatError(e)); setRunBusy(false); }
  };

  const seedDefaults = async () => {
    try {
      const { data } = await api.post("/admin/auto-news/seed-defaults");
      toast.success(`${data.added} default sources add हो गए (10 total)`);
      loadSources();
    } catch (e) { toast.error(formatError(e)); }
  };

  const toggleSetting = async (patch) => {
    try {
      const { data } = await api.post("/admin/auto-news/settings", { ...settings, ...patch });
      setSettings(data);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatError(e)); }
  };

  const toggleSourceActive = async (s) => {
    try {
      await api.patch(`/admin/auto-news/sources/${s._id || s.id}`, { active: !s.active });
      loadSources();
    } catch (e) { toast.error(formatError(e)); }
  };
  const toggleSourceAutoPub = async (s) => {
    try {
      await api.patch(`/admin/auto-news/sources/${s._id || s.id}`, { auto_publish: !s.auto_publish });
      loadSources();
    } catch (e) { toast.error(formatError(e)); }
  };
  const deleteSource = async (s) => {
    if (!confirm(`Delete source "${s.name}"?`)) return;
    try {
      await api.delete(`/admin/auto-news/sources/${s._id || s.id}`);
      toast.success("Deleted");
      loadSources();
    } catch (e) { toast.error(formatError(e)); }
  };
  const addSource = async () => {
    if (!newSrc.name.trim() || !newSrc.url.trim()) return toast.error("Name aur URL zaroori hai");
    try {
      await api.post("/admin/auto-news/sources", newSrc);
      toast.success("Source added");
      setNewSrc({ name: "", url: "", category: "राष्ट्रीय", auto_publish: false });
      loadSources();
    } catch (e) { toast.error(formatError(e)); }
  };

  const approve = async (q, edits = null) => {
    try {
      const payload = edits || {};
      const { data } = await api.post(`/admin/auto-news/queue/${q._id || q.id}/approve`, payload);
      toast.success("Published! Image background me generate hoga");
      setEditing(null);
      loadQueue();
      window.open(`/news/${data.slug}`, "_blank");
    } catch (e) { toast.error(formatError(e)); }
  };
  const reject = async (q) => {
    if (!confirm("Reject this article?")) return;
    try {
      await api.post(`/admin/auto-news/queue/${q._id || q.id}/reject`);
      loadQueue();
    } catch (e) { toast.error(formatError(e)); }
  };
  const deleteQ = async (q) => {
    if (!confirm("Delete queue item?")) return;
    try {
      await api.delete(`/admin/auto-news/queue/${q._id || q.id}`);
      loadQueue();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="auto-news-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-600 grid place-items-center shadow-lg">
          <Bot className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">AI Auto-News Ingestion</h1>
          <p className="text-sm text-slate-500">RSS + Google News → Claude Sonnet Hindi rewrite → auto-publish या review queue</p>
        </div>
        <Button data-testid="run-now-btn" onClick={runNow} disabled={runBusy} className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white">
          {runBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Running…</> : <><Zap className="w-4 h-4 mr-2"/>Run Now</>}
        </Button>
      </div>

      {/* Master switch */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-3">
          <Switch data-testid="autonews-master-switch" checked={!!settings.auto_news_enabled}
            onCheckedChange={(v) => toggleSetting({ auto_news_enabled: v })}/>
          <div>
            <div className="font-semibold text-slate-800">Auto-Ingest {settings.auto_news_enabled ? "ON" : "OFF"}</div>
            <div className="text-xs text-slate-500">Har {settings.auto_news_frequency_min} min cron chalega जब ON है</div>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <label className="text-xs font-semibold text-slate-600">Frequency:</label>
          <select data-testid="autonews-freq-select" value={settings.auto_news_frequency_min}
            onChange={(e) => toggleSetting({ auto_news_frequency_min: parseInt(e.target.value) })}
            className="border border-slate-200 rounded-md px-2 py-1.5 text-sm">
            <option value={15}>15 min</option>
            <option value={30}>30 min</option>
            <option value={60}>1 hour</option>
            <option value={120}>2 hours</option>
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 flex gap-1 overflow-x-auto">
        {TAB_LIST.map((t) => (
          <button key={t.id} data-testid={`autonews-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition flex items-center gap-1.5 ${
              tab === t.id ? "border-fuchsia-600 text-fuchsia-700" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>
            <t.icon className="w-4 h-4"/>{t.label}
            {t.id === "queue" && queue.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full bg-fuchsia-100 text-fuchsia-700 font-semibold">{queue.length}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "queue" && (
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <div className="text-sm text-slate-600">
              {queueLoading ? "Loading…" : queue.length === 0 ? "कोई pending article नहीं — 'Run Now' दबाओ ऊपर se" : `${queue.length} articles pending review`}
            </div>
            <Button variant="outline" size="sm" onClick={loadQueue}><RefreshCw className="w-3.5 h-3.5 mr-1"/>Refresh</Button>
          </div>
          {queue.map((q) => (
            <QueueCard key={q._id || q.id} q={q} onApprove={approve} onReject={reject} onDelete={deleteQ} editing={editing} setEditing={setEditing} />
          ))}
        </div>
      )}

      {tab === "sources" && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <Button data-testid="seed-defaults-btn" onClick={seedDefaults} className="bg-indigo-600 hover:bg-indigo-700 text-white">
              <Plus className="w-4 h-4 mr-2"/>Seed 10 Default Hindi Sources
            </Button>
            <div className="text-xs text-slate-500">Aaj Tak, Bhaskar, NDTV, Amar Ujala, Google News (Hindi) — sab pre-configured</div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><Plus className="w-4 h-4"/>Naya Source Add करें</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <Input data-testid="new-source-name" placeholder="Name (जैसे 'India TV Hindi')" value={newSrc.name} onChange={(e) => setNewSrc({ ...newSrc, name: e.target.value })}/>
              <Input data-testid="new-source-url" placeholder="RSS URL" value={newSrc.url} onChange={(e) => setNewSrc({ ...newSrc, url: e.target.value })} className="md:col-span-2"/>
              <select value={newSrc.category} onChange={(e) => setNewSrc({ ...newSrc, category: e.target.value })} className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={newSrc.auto_publish} onChange={(e) => setNewSrc({ ...newSrc, auto_publish: e.target.checked })}/>
                Auto-publish (skip review queue)
              </label>
              <Button data-testid="add-source-btn" onClick={addSource} className="ml-auto bg-emerald-600 hover:bg-emerald-700 text-white">Add Source</Button>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                <tr>
                  <th className="text-left p-3">Source</th>
                  <th className="text-left p-3">Category</th>
                  <th className="text-center p-3">Auto-Pub</th>
                  <th className="text-center p-3">Active</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sources.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-400">कोई source नहीं — "Seed Defaults" दबाओ</td></tr>}
                {sources.map((s) => (
                  <tr key={s._id || s.id} data-testid={`source-row-${s._id || s.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="p-3">
                      <div className="font-semibold text-slate-800">{s.name}</div>
                      <a href={s.url} target="_blank" rel="noreferrer" className="text-xs text-slate-400 hover:text-fuchsia-600 truncate max-w-[400px] block">{s.url}</a>
                    </td>
                    <td className="p-3"><span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-700">{s.category}</span></td>
                    <td className="p-3 text-center"><Switch checked={!!s.auto_publish} onCheckedChange={() => toggleSourceAutoPub(s)}/></td>
                    <td className="p-3 text-center"><Switch checked={!!s.active} onCheckedChange={() => toggleSourceActive(s)}/></td>
                    <td className="p-3 text-right">
                      <button onClick={() => deleteSource(s)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "runs" && (
        <div className="space-y-2">
          {runs.length === 0 && <div className="text-center py-8 text-slate-400 text-sm">कोई run अभी तक नहीं हुआ — 'Run Now' try करो</div>}
          {runs.map((r) => (
            <div key={r._id || r.id} className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs font-mono text-slate-400">{(r.run_id || "").slice(0, 10)}</span>
                <span className="text-xs text-slate-500">{(r.started_at || "").slice(0, 19).replace("T", " ")}</span>
                {r.manual && <span className="text-[10px] px-1.5 py-0.5 rounded bg-fuchsia-100 text-fuchsia-700 font-semibold">MANUAL</span>}
                <div className="ml-auto flex gap-3 text-xs">
                  <span className="text-slate-600">Fetched: <b>{r.fetched}</b></span>
                  <span className="text-emerald-700">Published: <b>{r.published}</b></span>
                  <span className="text-fuchsia-700">Queued: <b>{r.queued}</b></span>
                  <span className="text-slate-500">Dup: <b>{r.duplicates}</b></span>
                  {r.errors > 0 && <span className="text-red-600">Errors: <b>{r.errors}</b></span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "settings" && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
          <div className="text-sm text-slate-700">
            <div className="font-semibold mb-2">🤖 AI Pipeline Info</div>
            <ul className="space-y-1 text-slate-600 text-xs list-disc pl-4">
              <li><b>Text AI</b>: Claude Sonnet 4.5 (best Hindi quality, कॉपीराइट-safe rewrite)</li>
              <li><b>Image AI</b>: Gemini Nano Banana auto-generates matching photo har article ke liye</li>
              <li><b>Duplicate detection</b>: Same news repeat नहीं होगी — hash of link+title stored</li>
              <li><b>Category detection</b>: AI khud sahi category assign karta hai</li>
              <li><b>Auto-publish sources</b>: Sirf "auto_publish" toggle वाले sources direct publish होते hain; बाकी review queue mein aate hain</li>
              <li><b>Cron</b>: Backend har <b>{settings.auto_news_frequency_min} min</b> auto run karta hai जब master switch ON hai</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function QueueCard({ q, onApprove, onReject, onDelete, editing, setEditing }) {
  const isEd = editing === (q._id || q.id);
  const [t, setT] = useState(q.title);
  const [e, setE] = useState(q.excerpt);
  const [c, setC] = useState(q.content);
  const [cat, setCat] = useState(q.category);
  const [breaking, setBreaking] = useState(false);
  useEffect(() => { if (isEd) { setT(q.title); setE(q.excerpt); setC(q.content); setCat(q.category); setBreaking(false); } }, [isEd, q]);

  return (
    <div data-testid={`queue-item-${q._id || q.id}`} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-fuchsia-100 text-fuchsia-700 font-semibold uppercase">{q.category}</span>
            <span className="text-[10px] text-slate-400">via {q.source_name}</span>
            {q.source_url && <a href={q.source_url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-slate-700"><ExternalLink className="w-3 h-3"/></a>}
            <span className="ml-auto text-[10px] text-slate-400 font-mono">{(q.created_at || "").slice(11, 16)}</span>
          </div>
          {isEd ? (
            <div className="space-y-2">
              <Input value={t} onChange={(ev) => setT(ev.target.value)} className="font-bold text-base"/>
              <Textarea value={e} onChange={(ev) => setE(ev.target.value)} rows={2} placeholder="Excerpt"/>
              <Textarea value={c} onChange={(ev) => setC(ev.target.value)} rows={8} placeholder="Content"/>
              <div className="flex flex-wrap gap-2 items-center">
                <select value={cat} onChange={(ev) => setCat(ev.target.value)} className="border border-slate-200 rounded-md px-2 py-1.5 text-sm">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <label className="flex items-center gap-1.5 text-xs text-slate-700">
                  <input type="checkbox" checked={breaking} onChange={(ev) => setBreaking(ev.target.checked)}/>
                  🔴 Breaking
                </label>
              </div>
            </div>
          ) : (
            <>
              <div className="font-bold text-slate-900 text-base mb-1">{q.title}</div>
              <div className="text-sm text-slate-600 mb-2">{q.excerpt}</div>
              <div className="text-xs text-slate-500 line-clamp-3 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: q.content }}/>
              {q.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {q.tags.map((tg, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">#{tg}</span>)}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
        {isEd ? (
          <>
            <Button data-testid={`approve-edited-${q._id || q.id}`} size="sm" onClick={() => onApprove(q, { title: t, excerpt: e, content: c, category: cat, is_breaking: breaking })} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1"/>Save & Publish
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
          </>
        ) : (
          <>
            <Button data-testid={`approve-${q._id || q.id}`} size="sm" onClick={() => onApprove(q)} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Check className="w-3.5 h-3.5 mr-1"/>Approve & Publish
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(q._id || q.id)}>Edit</Button>
            <Button size="sm" variant="outline" onClick={() => onReject(q)} className="border-slate-200 text-slate-600"><X className="w-3.5 h-3.5 mr-1"/>Reject</Button>
            <Button size="sm" variant="ghost" onClick={() => onDelete(q)} className="ml-auto text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5"/></Button>
          </>
        )}
      </div>
    </div>
  );
}
